import IndicatorSnapshot from "components/company/valuation/IndicatorSnapshot";
import ValuationHistory from "components/company/valuation/ValuationHistory";
import { Separator } from "components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "components/ui/Sheet";
import { Provider } from "jotai";
import { useAmplitude } from "lib/amplitude/Amplitude";
import { useRouter } from "next/router";
import type {
  ISecurityCountry,
  IValuationIndicator,
  IValuationIndicatorValue,
  IValuationIndicatorVerbiageLanguage,
} from "types/graphqlTypes";

interface ISheetValuationHistoryProps {
  verbiageLanguage: IValuationIndicatorVerbiageLanguage;
  securityCountry: ISecurityCountry;
  tickerSymbol: string;
  indicator: IValuationIndicator;
  indicators: IValuationIndicatorValue[];
  tab: string;
  viewerCanSeeHistory: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSetIndicator?: (indicator: IValuationIndicator) => void;
  children: React.ReactElement;
}

export default function SheetValuationHistory({
  verbiageLanguage,
  securityCountry,
  tickerSymbol,
  indicator,
  indicators,
  tab,
  viewerCanSeeHistory,
  isOpen,
  setIsOpen,
  onSetIndicator,
  children,
}: ISheetValuationHistoryProps) {
  const { replace, locale } = useRouter();
  const amplitude = useAmplitude();

  const isUSALocale = locale?.startsWith("en");
  const sugestions = getSugestionIndicators(
    indicators.filter((node) => node.viewerCanSeeHistory),
    indicator,
  );

  return (
    <Sheet
      modal={true}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && viewerCanSeeHistory) {
          replace(
            {
              pathname: "/[country]/[tickerSymbol]/valuation",
              query: {
                country: securityCountry.toLocaleLowerCase(),
                tickerSymbol,
                tab,
              },
            },
            undefined,
            {
              shallow: true,
            },
          );
        }

        setIsOpen(open);
      }}
    >
      <SheetTrigger asChild={true}>{children}</SheetTrigger>

      <SheetContent
        position="right"
        className="h-full w-full space-y-4 overflow-y-auto border-none bg-white dark:bg-slate-900 xl:w-2/3"
        onOpenAutoFocus={(event) => {
          // See: https://github.com/radix-ui/primitives/discussions/935
          event.preventDefault();
        }}
      >
        <SheetHeader className="hidden">
          <SheetTitle>Valuation History</SheetTitle>
          <SheetDescription>Details</SheetDescription>
        </SheetHeader>

        <Provider>
          <ValuationHistory
            verbiageLanguage={verbiageLanguage}
            securityCountry={securityCountry}
            tickerSymbol={tickerSymbol}
            indicator={indicator}
          />
        </Provider>

        <Separator className="my-3 bg-gray-100 dark:bg-slate-800" />

        <div className="grid grid-cols-2 gap-x-2 lg:grid-cols-4">
          {sugestions.map((node, index) => (
            <div key={node.indicator} className="flex">
              <IndicatorSnapshot
                indicator={node.indicator}
                abbreviation={node.verbiage.abbreviation}
                description={node.verbiage.description}
                category={tab === "MY_INDICATORS" ? node.category : undefined}
                originalValue={node.value}
                verbiageLanguage={verbiageLanguage}
                formattedValue={node.formattedValue}
                isBookmarked={false}
                viewerCanBookmark={false}
                isAdjusted={String(node.indicator).includes("NORMALIZED")}
                viewerCanSeeHistory={viewerCanSeeHistory}
                onToggleActive={() => {
                  amplitude.logEvent("Viewed a valuation indicator", {
                    tickerSymbol: tickerSymbol.toUpperCase(),
                    indicator: node.indicator,
                    abbreviation: node.verbiage.abbreviation,
                    category: node.category,
                  });

                  window.history.pushState(
                    null,
                    "",
                    `/${securityCountry.toLocaleLowerCase()}/${tickerSymbol}/valuation/${node.indicator}?tab=${tab}`,
                  );

                  if (node.indicator && node.viewerCanSeeHistory && onSetIndicator) {
                    onSetIndicator(node.indicator);
                  }
                }}
              />
              {sugestions.length - 1 !== index && (
                <Separator orientation="vertical" className="bg-gray-100 dark:bg-slate-800" />
              )}
            </div>
          ))}
        </div>

        <SheetFooter className="justify-end sm:space-x-6">
          <div>
            <span className="rounded bg-gray-50 p-1 text-xs font-bold text-gray-600 dark:bg-slate-800 dark:text-slate-300">
              Esc
            </span>
            <span className="text-xs font-medium italic text-gray-400 dark:text-slate-400">
              {isUSALocale ? "—to close" : "—para fechar essa página"}
            </span>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function getSugestionIndicators(indicators: IValuationIndicatorValue[], isIndicator: IValuationIndicator) {
  const valuationIndicator = indicators.find((node) => node.indicator === isIndicator);
  const indicatorsLength = indicators.length - 1;

  if (!valuationIndicator) {
    return [];
  }

  const indicatorIndex = indicators.indexOf(valuationIndicator);

  // If it's the first position, take the next 4.
  if (indicatorIndex === 0) {
    return indicators.slice(1, 5);
  }

  if (indicatorIndex === 1) {
    return [indicators[0], ...indicators.slice(2, 5)];
  }

  // If it's the last position, take the last 4.
  if (indicatorIndex === indicatorsLength) {
    return indicators.slice(indicatorsLength - 4, indicatorsLength);
  }

  if (indicatorIndex === indicatorsLength - 1) {
    return [...indicators.slice(indicatorsLength - 4, indicatorsLength - 1), indicators[indicatorsLength]];
  }

  return [
    ...indicators.slice(indicatorIndex - 2, indicatorIndex),
    ...indicators.slice(indicatorIndex + 1, indicatorIndex + 3),
  ];
}
